import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Chips } from 'primereact/chips';
import { MultiSelect } from 'primereact/multiselect';
import { Link } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import Dropzone from 'react-dropzone';
import ImageComponent from 'shared/ImageComponent';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import { useLoaderDispatch } from 'context/LoaderContext';
import { useUserDetailsContext, UserDetailsContext } from 'context/userDetailsContext';
import { useNavbarContext } from 'context/NavbarContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { sanitizeHtmlContent } from '../utils/utils';
import { sanitizeHtmlColorContent } from 'utils/utils';
import { uploadFiles } from 'utils/filesUtils';
import { presignedURLGet } from 'modules/file_manager/services';
import useSendMail from '../hooks/useSendMail';

const MAX_FILES = 20;

/**
 * Container that shows subject header, case/lead link, and wraps thread items.
 */
const MailPreviewContainerV2 = ({ subject, caseId, caseName, isLead, isFromMailLogs, setShowMapping, children }) => (
  <div className="card-bg p-3 mail-preview-inner max-height-90" style={{ overflowY: 'auto' }}>
    {!caseId && !isFromMailLogs && (
      <div className="d-flex justify-content-between flex-wrap">
        <h1 className="m-2 sub-title">{subject}</h1>
        <span className="d-flex align-items-center text-hint gap-1">
          <i className="pi pi-info-circle" aria-hidden="true" />
          To reply, please map this email to a Case/Lead
        </span>
      </div>
    )}
    {caseId && (
      <div>
        <h1 className="m-2 sub-title">{subject}</h1>
        <div className="d-flex align-items-center">
          <span className="ms-2 me-2 text-medium">Case/Lead:</span>
          <Link to={`${isLead ? '/leads' : '/cases'}/${caseId}/mails`} className="redirection-link-no-text-transform ellipsis-text">
            <span className="redirection-link-no-text-transform case-name-tooltip-v2">{caseName}</span>
          </Link>
          <Tooltip target=".case-name-tooltip-v2" className="case-tooltip" content={caseName} position="top" />
        </div>
      </div>
    )}
    {isFromMailLogs && (
      <div className="d-flex justify-content-end">
        <Button
          type="button"
          className="p-button p-component p-button-primary button outline ms-auto mb-2"
          label="Map to Case/Lead"
          onClick={() => setShowMapping?.(true)}
        />
      </div>
    )}
    <div className="w-100 h-100 overflow-y-auto mail-threads">{children}</div>
  </div>
);

/**
 * Single email item (thread parent or reply) using V2 field names:
 *   from_email  → string
 *   to_email    → string[]
 *   cc_email    → string[]
 *   email_body  → HTML string
 *   mail_date   → ISO timestamp
 *   attachments → { filename, size, mime_type, attachment_url }[]
 */
const MailItemV2 = memo(({ mailItem, index, numberOfThreads, caseDetails, existingFiles, handleEmailData, setQuery }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    trigger,
  } = useForm();

  const { sendMail } = useSendMail();
  const { userDetails } = useUserDetailsContext();
  const setLoader = useLoaderDispatch();
  const userContext = useContext(UserDetailsContext);
  const { addToast } = useToast();
  const chipsRef = useRef(null);
  const { downloadFile: downloadAs } = useNavbarContext();

  const [showReplyBox, setShowReplyBox] = useState(false);
  const [showFullContent, setShowFullContent] = useState(index === numberOfThreads - 1);
  const [files, setFiles] = useState([]);

  const isSent = mailItem?.is_sent ?? false;

  // Derive recipients and sender from email_members (same as V1)
  const recipients = isSent ? mailItem?.email_members ?? [] : (mailItem?.email_members ?? []).filter((m) => !m.is_sender);

  const replyRecipients = isSent ? mailItem?.email_members ?? [] : (mailItem?.email_members ?? []).filter((m) => m.is_sender);

  const ccList = mailItem?.cc_list ?? [];

  const [chipEmails, setChipEmails] = useState(replyRecipients.map((m) => m?.user_email));

  useEffect(() => {
    setChipEmails(replyRecipients.map((m) => m?.user_email));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReplyBox]);

  const toggleReplyBox = () => {
    setShowReplyBox((prev) => !prev);
    reset();
  };

  const onSubmit = async (data) => {
    if (files.length > 0) {
      setLoader(true);
      try {
        const uploaded = await uploadFiles(files, 'email/outbound', caseDetails?.case_id, userContext?.firmDetails?.current_user);
        data.attachments = [...(data.attachments ?? []), ...uploaded];
      } catch (err) {
        console.error('[MailItemV2] file upload error:', err);
      } finally {
        setLoader(false);
      }
    }

    const formattedData = {
      ...data,
      subject: mailItem?.subject,
      email_content: data?.email_content,
      cc_list: [],
      bcc_list: [],
      reply_to: mailItem?.email_message_id ?? undefined,
      case_id: caseDetails?.case_id,
      case_name: caseDetails?.case_description?.case_name,
      is_lead: caseDetails?.is_lead ?? false,
      recipient_list: chipEmails,
      reply_reference_pk: mailItem?.pk,
      reply_reference_sk: mailItem?.sk,
    };

    const recipientMap = new Map((mailItem?.email_members ?? []).map((m) => [m?.user_email, m?.user_name]));
    const recipientListWithUserNames = chipEmails.map((email) => ({
      user_email: email,
      user_name: recipientMap.get(email) || email,
    }));

    const allData = {
      ...mailItem,
      email_members: recipientListWithUserNames,
      email_from: {
        user_email: userDetails?.business_email,
        user_name: `${userDetails?.first_name ?? ''} ${userDetails?.last_name ?? ''}`.trim(),
      },
      email_content: data?.email_content,
      time: new Date().toISOString(),
      attachments: data?.attachments ?? [],
    };

    const response = await sendMail(formattedData);
    if (response?.status === 200) {
      handleEmailData?.(allData);
      toggleReplyBox();
    }
  };

  const onChangeFile = (acceptedFiles) => {
    const total = [...files, ...acceptedFiles];
    if (total.length > MAX_FILES) {
      addToast(false, toastConstant.toasterType.WARN, toastConstant.header.WARNINIG, toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED);
      return;
    }
    setFiles(total);
  };

  const downloadHandler = async (file) => {
    // V2 attachment_url is an S3 path; derive file_path & file_name
    const filePath = file?.attachment_url ?? file?.file_path;
    const fileName = file?.filename ?? file?.file_name;
    const data = await presignedURLGet(filePath);
    const url = data?.data?.file_url;
    downloadAs(url, fileName);
  };

  return (
    <div className="py-3 px-2 border-bottom">
      {/* Header */}
      <div className="w-100">
        <div className="d-flex flex-row justify-content-between py-2 pointer">
          <div
            className="d-flex gap-2 flex-row align-items-center w-100"
            onClick={() => setShowFullContent((v) => !v)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setShowFullContent((v) => !v)}
            aria-expanded={showFullContent}
          >
            <div className="d-flex flex-column w-100 gap-1">
              <div className="d-flex flex-row w-100 gap-2">
                <ImageComponent
                  fileName={mailItem?.email_from?.user_email?.charAt(0)?.toUpperCase() || '?'}
                  style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                  fileSize="medium.jpg"
                />
                <div className="d-flex flex-row w-100 justify-content-between">
                  <div className="w-100 d-flex flex-row align-items-center flex-wrap gap-2">
                    <h5 className="caption-bold mb-0">{mailItem?.email_from?.user_name || mailItem?.email_from?.user_email}</h5>
                    <span className="text-hint cursor-text m-0">
                      {'<'}
                      {mailItem?.email_from?.user_email}
                      {'>'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-row w-100 justify-content-between flex-wrap">
                <div className="gap-1">
                  <div className="d-flex text-hint gap-1 w-100">
                    <span>to:</span>
                    <span className="cursor-text">{recipients.map((m) => m?.user_email).join(', ')}</span>
                  </div>
                  {ccList.length > 0 && (
                    <div className="d-flex text-hint gap-1">
                      <span>cc:</span>
                      <span className="cursor-text">{ccList.join(', ')}</span>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-hint black-600">
                    {convertDateFromApiToUserTimezone(mailItem?.created_at, userDetails?.timezone, 'MM-DD-YYYY, hh:mm A')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      {showFullContent && (
        <div className="my-3 mail-preview-container">
          {mailItem?.email_content ? (
            <div className="d-flex flex-column gap-2 mb-2">
              <span dangerouslySetInnerHTML={sanitizeHtmlContent(sanitizeHtmlColorContent(mailItem.email_content))} />
            </div>
          ) : (
            <span>No Content</span>
          )}

          {/* Attachments */}
          {mailItem?.attachments?.length > 0 && (
            <>
              <div className="mt-4 pt-2 mb-2 p-medium">Attachments</div>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {mailItem.attachments.map((file, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => downloadHandler(file)}
                    className="d-flex align-items-center text-primary-dark pointer border-0 bg-transparent p-0"
                    aria-label={`Download ${file?.filename ?? file?.file_name}`}
                  >
                    <i className="pi pi-file me-1" aria-hidden="true" />
                    {file?.filename ?? file?.file_name}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Reply button — only if email is mapped to a case */}
          {caseDetails?.case_id && caseDetails?.case_description?.case_name && (
            <div className="col-12 d-flex justify-content-end">
              <Button
                label="Reply"
                onClick={toggleReplyBox}
                className="btn-outline p-button-primary"
                icon="pi pi-reply"
                disabled={!caseDetails?.case_id}
              />
            </div>
          )}

          {/* Reply box */}
          {showReplyBox && (
            <div className="w-100 mt-3 border card-bg p-3">
              <div className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  <span className="me-2">To </span>
                  <Controller
                    name="recipient_list"
                    control={control}
                    rules={{
                      required: chipEmails.length === 0 ? 'Required' : false,
                      validate: (value) => {
                        if (!value) return true;
                        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                        const internalDomainRegex = /@.*\.lawft\.com$/i;
                        for (const email of value) {
                          const trimmed = email?.trim();
                          if (trimmed === userDetails?.business_email) return 'You cannot send an email to your own address';
                          if (internalDomainRegex.test(trimmed)) return 'You cannot send email to internal users';
                          if (!emailRegex.test(trimmed)) return `Invalid email format: ${email}`;
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <Chips
                        ref={chipsRef}
                        inputRef={field.ref}
                        id={field.name}
                        value={chipEmails}
                        onChange={(e) => {
                          setChipEmails(e.value);
                          field.onChange(e.value);
                        }}
                        placeholder={chipEmails.length ? '' : 'To'}
                        className="w-100 input-shadow taging"
                        separator=","
                        onBlur={(e) => {
                          if (!e?.target?.value) return;
                          const updated = [...(chipEmails ?? []), e.target.value];
                          setChipEmails(updated);
                          field.onChange(updated);
                          if (chipsRef?.current?.element?.querySelector('input')) {
                            chipsRef.current.element.querySelector('input').value = '';
                          }
                          trigger('recipient_list');
                        }}
                      />
                    )}
                  />
                </div>
                <Button
                  icon="icon icon-delete mx-1 cursor-pointer F-size16"
                  className="p-button-danger p-button-text"
                  onClick={toggleReplyBox}
                  aria-label="Close reply"
                />
              </div>
              {errors.recipient_list && <InputErrorMessage>{errors.recipient_list.message}</InputErrorMessage>}

              <div className="py-3 d-flex flex-column gap-4 email-quill-reply">
                <Controller
                  control={control}
                  name="email_content"
                  render={({ field: { onChange, value } }) => (
                    <div style={{ minHeight: '200px' }}>
                      <TextSnippetWrapper
                        className="email-quill-editor shadow-small"
                        type="editor"
                        value={sanitizeHtmlContent(value)?.__html || ''}
                        onChange={(e) => onChange(e)}
                      />
                    </div>
                  )}
                />

                {files.length > 0 && (
                  <ul className="list-unstyled">
                    {files.map((f, i) => (
                      <div key={i} className="d-flex align-items-center">
                        <i className="fas fa-paperclip me-2 mt-1" aria-hidden="true" />
                        <li className="text-break ms-1">{f.name}</li>
                        <button
                          type="button"
                          className="icon-delete ms-3 pointer border-0 bg-transparent p-0"
                          aria-label={`Remove ${f.name}`}
                          onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                        />
                      </div>
                    ))}
                  </ul>
                )}

                <div className="col-12 px-2 drag-drop-box d-flex flex-wrap justify-content-center align-items-center">
                  <Dropzone onDrop={onChangeFile} accept="application/pdf, application/msword, .docx, .doc, audio/*, video/*, image/*">
                    {({ getRootProps, getInputProps }) => (
                      <section className="d-flex align-items-center">
                        <div {...getRootProps()}>
                          <input {...getInputProps()} />
                          <p className="text-center mb-0">Click or drag file to this area to upload</p>
                        </div>
                      </section>
                    )}
                  </Dropzone>
                  <p className="mb-0 d-flex align-items-center">
                    <i>or</i>
                  </p>
                  <Controller
                    name="attachments"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <MultiSelect
                        options={existingFiles?.length ? existingFiles : [{ label: 'No Records Found', value: 'no-records-found' }]}
                        className="input-shadow existing-files"
                        placeholder="Select or Search Existing Files"
                        display="chip"
                        value={value || []}
                        filter
                        onChange={(e) => onChange(e.value)}
                        optionLabel="label"
                        optionValue="value"
                        optionDisabled={(item) => item?.value === 'no-records-found'}
                        showClear
                        resetFilterOnHide
                        id={name}
                        onFilter={(e) => setQuery?.(e?.filter)}
                        emptyFilterMessage="No records found"
                        showSelectAll={false}
                      />
                    )}
                  />
                </div>

                <Button
                  label="Send"
                  className="p-button-primary"
                  style={{ alignSelf: 'flex-end' }}
                  icon="pi pi-send"
                  onClick={handleSubmit(onSubmit)}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

MailItemV2.displayName = 'MailItemV2';

/**
 * MailPreviewV2 — renders all emails in a selected thread using V2 field names.
 */
const MailPreviewV2 = ({ previewMail, caseDetails, existingFiles, handleEmailData, setQuery, isFromMailLogs, setShowMapping }) => {
  const numberOfThreads = previewMail?.length ?? 0;

  if (!numberOfThreads) {
    return (
      <div className="card-bg p-3 h-100 d-flex align-items-center justify-content-center">
        <span className="text-muted">No thread data available.</span>
      </div>
    );
  }

  return (
    <MailPreviewContainerV2
      subject={previewMail[0]?.subject}
      caseId={caseDetails?.case_id}
      caseName={caseDetails?.case_description?.case_name}
      isLead={caseDetails?.is_lead}
      isFromMailLogs={isFromMailLogs}
      setShowMapping={setShowMapping}
    >
      {previewMail.map((mailItem, index) => (
        <MailItemV2
          key={`${mailItem?.pk}-${index}`}
          mailItem={mailItem}
          index={index}
          numberOfThreads={numberOfThreads}
          caseDetails={caseDetails}
          existingFiles={existingFiles}
          setQuery={setQuery}
          handleEmailData={handleEmailData}
        />
      ))}
    </MailPreviewContainerV2>
  );
};

MailPreviewV2.propTypes = {
  previewMail: PropTypes.arrayOf(
    PropTypes.shape({
      pk: PropTypes.string,
      sk: PropTypes.string,
      email_from: PropTypes.shape({
        user_name: PropTypes.string,
        user_email: PropTypes.string,
      }),
      email_members: PropTypes.arrayOf(
        PropTypes.shape({
          is_sender: PropTypes.bool,
          user_name: PropTypes.string,
          user_email: PropTypes.string,
        })
      ),
      cc_list: PropTypes.arrayOf(PropTypes.string),
      email_content: PropTypes.string,
      subject: PropTypes.string,
      created_at: PropTypes.string,
      is_sent: PropTypes.bool,
      attachments: PropTypes.array,
      res_type: PropTypes.string,
    })
  ),
  caseDetails: PropTypes.object,
  existingFiles: PropTypes.array,
  handleEmailData: PropTypes.func,
  setQuery: PropTypes.func,
  isFromMailLogs: PropTypes.bool,
  setShowMapping: PropTypes.func,
};

MailPreviewV2.defaultProps = {
  previewMail: [],
  caseDetails: {},
  existingFiles: [],
  handleEmailData: () => {},
  setQuery: () => {},
  isFromMailLogs: false,
  setShowMapping: () => {},
};

export default MailPreviewV2;
